/* eslint-disable react/no-array-index-key */
import { useState, useEffect } from "react";
import Papa from "papaparse";
import {
  Box,
  Typography,
  Container,
  Paper,
  FormControlLabel,
  Checkbox as MuiCheckbox,
} from "@mui/material";
import TableViewIcon from "@mui/icons-material/TableView";
import { getCountries } from "../api/Countries";
import { addConfiguration } from "../api/ConfigService";
import TextField from "./primitives/TextField";
import Dropdown from "./primitives/Dropdown";
import Checkbox from "./primitives/Checkbox";
import Button from "./primitives/Button";
import CsvUpload from "./CsvUpload";
import CsvMapping from "./CsvMapping";
import { useAlert } from "../hook/useAlert";
import AutoCompleteDropdown from "./primitives/AutoCompleteDropdown";
import { useUser } from "../context/UserContext";

const BatchForm = ({ onFileUpload }) => {
  const { onError, onInfo, onSuccess } = useAlert();
  const { user } = useUser();

  const defaultSchemaAttributes = [
    "email",
    "dateOfBirth",
    "firstName",
    "lastName",
    "country",
    "phoneNumber",
    "createdAt",
    "language",
  ];
  const enrichmentTypes = [
    {
      value: "brand_preferences",
      display: "Brand Preference",
    },
    {
      value: "passion_points",
      display: "Passion Point",
    },
    {
      value: "weekly_plus",
      display: "Weekly Plus",
    },
    {
      value: "form_name",
      display: "Form Name",
    },
  ];
  const commPrefs = ["email", "sms", "phone", "whatsapp"];
  const consentTypes = ["marketing", "personalization"];

  const [countries, setCountries] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [csvData, setCsvData] = useState([]);
  const [csvHeaders, setCsvHeaders] = useState([]);
  const [isValidated, setIsValidated] = useState(false);
  const [fieldMappings, setFieldMappings] = useState([]);
  const [unmappedHeaders, setUnmappedHeaders] = useState([]);
  const [enrichmentMappings, setEnrichmentMappings] = useState([]);
  const [consentMappings, setConsentMappings] = useState([]);
  const [isMarketingConsentImplied, setIsMarketingConsentImplied] = useState(false);
  const [isDialingCodeEdited, setIsDialingCodeEdited] = useState(false);
  const [commPrefsState, setCommPrefsState] = useState([]);
  const [formValues, setFormValues] = useState({
    country: "",
    campaignName: "",
    minAge: 14,
    maxAge: 120,
    hashIdentifier: "email",
    status: "active",
    platform: "batch",
    dialingCode: "",
  });
  const [validationErrors, setValidationErrors] = useState([]);

  const normalizeUnique = (values = []) => {
    return [...new Set(values.map((value) => String(value).trim().toLowerCase()).filter(Boolean))];
  };

  const getSchemaFieldByHashIdentifier = (hashIdentifier) => {
    const mapping = {
      phone: "phoneNumber",
      email: "email",
    };
    return mapping[hashIdentifier] || hashIdentifier;
  };

  const validateRequiredMappings = () => {
    const requiredFields = ["dateOfBirth", "country"];
    const mappedSchemaValues = fieldMappings.map((mapping) => mapping.schemaValue);
    const missingRequired = requiredFields.filter((field) => !mappedSchemaValues.includes(field));

    return missingRequired;
  };

  const validateHashIdentifierMapping = () => {
    const hashIdentifierField = formValues.hashIdentifier;
    const requiredSchemaField = getSchemaFieldByHashIdentifier(hashIdentifierField);
    const mappedSchemaValues = fieldMappings.map((mapping) => mapping.schemaValue);

    return !mappedSchemaValues.includes(requiredSchemaField);
  };

  const handleInputChange = (event) => {
    const { name, value, type } = event.target;
    const val = type === "number" ? Number(value) : value;

    if (name === "dialingCode") {
      setIsDialingCodeEdited(true);
    }

    setFormValues({ ...formValues, [name]: val });
    setValidationErrors([]);
  };

  const handleCommPrefsChange = (event) => {
    const { value, checked } = event.target;
    const normalizedValue = value.toLowerCase();

    setCommPrefsState((prevPrefs) => {
      if (checked) {
        return normalizeUnique([...prevPrefs, normalizedValue]);
      }
      return prevPrefs.filter((pref) => pref !== normalizedValue);
    });
    setValidationErrors([]);
  };

  const processCsvData = () => {
    return csvData.map((row) => {
      const newRow = {};

      fieldMappings.forEach((item) => {
        newRow[item.schemaValue] = row[item.customValue];
      });

      unmappedHeaders.forEach((header) => {
        newRow[header] = row[header];
      });

      return newRow;
    });
  };

  const resetToDefaults = () => {
    setFormValues({
      country: "",
      campaignName: "",
      minAge: 14,
      maxAge: 120,
      hashIdentifier: "email",
      status: "active",
      platform: "batch",
      dialingCode: "",
    });
    setCommPrefsState([]);
    setCsvFile(null);
    setCsvData([]);
    setCsvHeaders([]);
    setIsValidated(false);
    setFieldMappings([]);
    setUnmappedHeaders([]);
    setEnrichmentMappings([]);
    setConsentMappings([]);
    setIsMarketingConsentImplied(false);
    setIsDialingCodeEdited(false);
    setValidationErrors([]);
    if (onFileUpload) {
      onFileUpload(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsLoading(true);
    setValidationErrors([]);

    try {
      processCsvData();

      const mappedConsents = consentMappings
        .map((consent) => {
          const consentType =
            consent.consentType === "custom"
              ? String(consent.customConsentType || "").trim()
              : consent.consentType;

          return {
            consentType,
            header: consent.header,
          };
        })
        .filter((consent) => consent.header && consent.consentType);

      const hasMarketingConsent = mappedConsents.some(
        (consent) => consent.consentType.toLowerCase() === "marketing"
      );

      if (!hasMarketingConsent && !isMarketingConsentImplied) {
        onError(
          "Marketing consent is required. Map a CSV header to marketing consent or mark it as implied."
        );
        setIsLoading(false);
        return;
      }

      const payload = {
        ...formValues,
        enrichments: enrichmentMappings,
        fieldMappings,
        commPrefs: normalizeUnique(commPrefsState),
        consents:
          !hasMarketingConsent && isMarketingConsentImplied
            ? [...mappedConsents, "marketing"]
            : mappedConsents,
        region: user?.region,
      };

      if (payload.commPrefs.length === 0) {
        onError("At least one communication preference must be selected.");
        setIsLoading(false);
        return;
      }

      const userName = sessionStorage.getItem("userName");

      const res = await addConfiguration({ ...payload, userName });
      onSuccess(res.message);

      resetToDefaults();
    } catch (error) {
      if (error.errors && Array.isArray(error.errors)) {
        setValidationErrors(error.errors);
        onError(error.message);
      } else {
        onError(error.message || "An error occurred");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const resetCsvMappingState = () => {
    setCsvData([]);
    setCsvHeaders([]);
    setIsValidated(false);
    setFieldMappings([]);
    setUnmappedHeaders([]);
    setEnrichmentMappings([]);
    setConsentMappings([]);
    setValidationErrors([]);
  };

  const handleFileSelect = (e) => {
    e.preventDefault();
    const file = e.target.files[0];
    if (file) {
      setCsvFile(file);
      resetCsvMappingState();
      if (onFileUpload) {
        onFileUpload(true);
      }
    } else {
      onInfo("Please upload a valid CSV file.");
    }
  };

  const handleFileDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) {
      setCsvFile(file);
      resetCsvMappingState();
      if (onFileUpload) {
        onFileUpload(true);
      }
    } else {
      onInfo("Please upload a valid CSV file.");
    }
  };

  const processCsv = () => {
    if (!csvFile) {
      onInfo("Please upload a CSV file before processing.");
      return;
    }

    resetCsvMappingState();

    Papa.parse(csvFile, {
      header: true,
      dynamicTyping: false,
      skipEmptyLines: true,
      complete: (results) => {
        const headers = results.meta.fields;
        const processedData = results.data.map((row) => {
          return Object.entries(row).reduce((newRow, [key, value]) => {
            if (value !== "") {
              newRow[key] = value;
            }
            return newRow;
          }, {});
        });
        setCsvHeaders(headers);
        setCsvData(processedData);
        setUnmappedHeaders(headers);
      },
      error: (error) => {
        onError("Error parsing CSV file: ", error);
      },
    });
  };

  const onDragEnd = (result) => {
    const { source, destination, draggableId } = result;
    if (!destination) return;

    const startDroppableId = source.droppableId;
    const finishDroppableId = destination.droppableId;

    if (startDroppableId === finishDroppableId && source.index === destination.index) {
      return;
    }

    setValidationErrors([]);

    const newUnmappedHeaders = Array.from(unmappedHeaders);
    const newFieldMappings = Array.from(fieldMappings);
    const newEnrichmentMappings = Array.from(enrichmentMappings);
    const newConsentMappings = Array.from(consentMappings);

    if (startDroppableId === "unmapped") {
      newUnmappedHeaders.splice(source.index, 1);
    } else if (startDroppableId === "enrichment") {
      newEnrichmentMappings.splice(source.index, 1);
    } else if (startDroppableId === "consents") {
      newConsentMappings.splice(source.index, 1);
    } else {
      const index = newFieldMappings.findIndex(
        (item) => item.schemaValue === startDroppableId && item.customValue === draggableId
      );
      if (index !== -1) {
        newFieldMappings.splice(index, 1);
      }
    }

    if (finishDroppableId === "unmapped") {
      newUnmappedHeaders.splice(destination.index, 0, draggableId);
    } else if (finishDroppableId === "enrichment") {
      newEnrichmentMappings.splice(destination.index, 0, {
        header: draggableId,
        category: "",
        type: "",
      });
    } else if (finishDroppableId === "consents") {
      newConsentMappings.splice(destination.index, 0, {
        header: draggableId,
        consentType: "marketing",
        customConsentType: "",
      });
    } else {
      const exampleData = csvData.length > 0 ? csvData[0][draggableId] : null;

      newFieldMappings.push({
        schemaValue: finishDroppableId,
        customValue: draggableId,
        exampleData,
      });
    }

    setUnmappedHeaders(newUnmappedHeaders);
    setFieldMappings(newFieldMappings);
    setEnrichmentMappings(newEnrichmentMappings);
    setConsentMappings(newConsentMappings);
    setIsValidated(true);
  };

  const handleEnrichmentChange = (index, field, value) => {
    const updatedEnrichmentMappings = [...enrichmentMappings];
    updatedEnrichmentMappings[index][field] = value;
    setEnrichmentMappings(updatedEnrichmentMappings);
    setValidationErrors([]);
  };

  const handleConsentMappingChange = (index, field, value) => {
    const updatedConsentMappings = [...consentMappings];
    updatedConsentMappings[index][field] = value;
    setConsentMappings(updatedConsentMappings);
    setValidationErrors([]);
  };

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const countryList = await getCountries();
        setCountries(countryList.sort((a, b) => a.name.localeCompare(b.name)));
      } catch (error) {
        onError("Error fetching countries: ", error);
      }
    };

    fetchCountries();
  }, []);

  useEffect(() => {
    const hasPhoneNumberMapping = fieldMappings.some(
      (mapping) => mapping.schemaValue === "phoneNumber"
    );
    const shouldPopulateDialingCode =
      (formValues.hashIdentifier === "phone" || hasPhoneNumberMapping) && countries.length > 0;

    if (shouldPopulateDialingCode) {
      const selectedCountry = countries.find((country) => country.code === formValues.country);
      if (selectedCountry && selectedCountry.dialingCode) {
        if (!isDialingCodeEdited) {
          setFormValues((prevFormValues) => ({
            ...prevFormValues,
            dialingCode: selectedCountry.dialingCode,
          }));
        }
      } else if (!isDialingCodeEdited) {
        setFormValues((prevFormValues) => ({
          ...prevFormValues,
          dialingCode: "",
        }));
      }
    } else if (!isDialingCodeEdited) {
      setFormValues((prevFormValues) => ({
        ...prevFormValues,
        dialingCode: "",
      }));
    }
  }, [
    formValues.country,
    formValues.hashIdentifier,
    countries,
    isDialingCodeEdited,
    fieldMappings,
  ]);

  useEffect(() => {
    if (formValues.hashIdentifier === "email") {
      setCommPrefsState((prevPrefs) =>
        normalizeUnique(prevPrefs.includes("email") ? prevPrefs : [...prevPrefs, "email"])
      );
    } else if (formValues.hashIdentifier === "phone") {
      setCommPrefsState((prevPrefs) => {
        const newPrefs = [...prevPrefs, "phone", "sms"];
        return normalizeUnique(newPrefs);
      });
    }
  }, [formValues.hashIdentifier]);

  const missingRequired = validateRequiredMappings();
  const missingHashMapping = validateHashIdentifierMapping();
  const isDialingCodeRequired = formValues.hashIdentifier === "phone" && !formValues.dialingCode;
  const missingCommPreference = normalizeUnique(commPrefsState).length === 0;
  const hasMarketingConsentMapping = consentMappings.some((consent) => {
    const consentType =
      consent.consentType === "custom"
        ? String(consent.customConsentType || "")
            .trim()
            .toLowerCase()
        : String(consent.consentType || "")
            .trim()
            .toLowerCase();
    return Boolean(consent.header) && consentType === "marketing";
  });
  const missingMarketingConsent = !hasMarketingConsentMapping && !isMarketingConsentImplied;

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h5" mb={2} display="flex" alignItems="center">
          <TableViewIcon sx={{ mr: 1, fontSize: 30 }} />
          Batch Configuration Details
        </Typography>
        <Box sx={{ flex: 1, width: "100%" }}>
          <AutoCompleteDropdown
            label="Country:"
            id="country"
            name="country"
            value={formValues.country}
            onChange={(e) => {
              setIsDialingCodeEdited(false);
              handleInputChange(e);
            }}
            options={[
              ...countries.map((country) => ({
                value: country.code,
                label: `${country.name} (${country.code})`,
              })),
            ]}
          />
          <TextField
            label="Campaign Name:"
            id="campaignName"
            name="campaignName"
            value={formValues.campaignName}
            onChange={handleInputChange}
          />
          <TextField
            type="number"
            label="Minimum Age:"
            id="minAge"
            name="minAge"
            value={formValues.minAge}
            onChange={handleInputChange}
            inputProps={{ min: 14, max: 120 }}
          />
          <TextField
            type="number"
            label="Maximum Age:"
            id="maxAge"
            name="maxAge"
            value={formValues.maxAge}
            onChange={handleInputChange}
            inputProps={{ min: 14, max: 120 }}
          />
          <Dropdown
            label="Hash Identifier:"
            id="hashIdentifier"
            name="hashIdentifier"
            value={formValues.hashIdentifier}
            onChange={(e) => {
              setIsDialingCodeEdited(false);
              handleInputChange(e);
            }}
            options={[
              { value: "email", label: "Email" },
              { value: "phone", label: "Phone Number" },
            ]}
          />
          {formValues.hashIdentifier === "phone" && (
            <TextField
              label="Dialing Code:"
              id="dialingCode"
              name="dialingCode"
              value={formValues.dialingCode}
              onChange={handleInputChange}
            />
          )}
          <Checkbox
            label="Communication Preferences:"
            name="commPrefs"
            options={commPrefs}
            selectedOptions={commPrefsState}
            onChange={handleCommPrefsChange}
          />
          <CsvUpload
            csvFile={csvFile}
            onFileSelect={handleFileSelect}
            onFileDrop={handleFileDrop}
            onProcessCsv={processCsv}
          />
          {csvHeaders.length > 0 && (
            <>
              <Box sx={{ mb: 2, p: 2, backgroundColor: "#f5f5f5", borderRadius: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 1 }}>
                  Mapping Requirements:
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  • <strong>Always required:</strong> Date of Birth, Country
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  • <strong>Hash identifier mapping:</strong>
                  {formValues.hashIdentifier === "email" ? "Email" : "Phone Number"} must be mapped
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  • <strong>Consent mapping:</strong> Drag CSV headers to Consent Fields and select
                  consent type (marketing/personalization/custom)
                </Typography>
                <FormControlLabel
                  sx={{ mt: 1 }}
                  control={
                    <MuiCheckbox
                      checked={isMarketingConsentImplied}
                      onChange={(e) => setIsMarketingConsentImplied(e.target.checked)}
                    />
                  }
                  label="Marketing consent is implied (default granted = 1)"
                />
              </Box>
              <CsvMapping
                unmappedHeaders={unmappedHeaders}
                fieldMappings={fieldMappings}
                enrichmentMappings={enrichmentMappings}
                consentMappings={consentMappings}
                defaultSchemaAttributes={defaultSchemaAttributes}
                enrichmentTypes={enrichmentTypes}
                onDragEnd={onDragEnd}
                handleEnrichmentChange={handleEnrichmentChange}
                handleConsentMappingChange={handleConsentMappingChange}
                availableConsentTypes={consentTypes}
                formValues={formValues}
                validationErrors={validationErrors}
              />
              {(missingRequired.length > 0 ||
                missingHashMapping ||
                isDialingCodeRequired ||
                missingCommPreference ||
                missingMarketingConsent) && (
                <Box sx={{ mt: 2, mb: 1 }}>
                  {missingRequired.length > 0 && (
                    <Typography variant="body2" color="error" sx={{ mb: 1 }}>
                      ⚠️ Required mappings missing: {missingRequired.join(", ")}
                    </Typography>
                  )}
                  {missingHashMapping && (
                    <Typography variant="body2" color="error" sx={{ mb: 1 }}>
                      ⚠️ {formValues.hashIdentifier === "email" ? "Email" : "Phone Number"} must be
                      mapped (hash identifier)
                    </Typography>
                  )}
                  {isDialingCodeRequired && (
                    <Typography variant="body2" color="error" sx={{ mb: 1 }}>
                      ⚠️ Dialing code is required when using Phone Number as hash identifier
                    </Typography>
                  )}
                  {missingCommPreference && (
                    <Typography variant="body2" color="error" sx={{ mb: 1 }}>
                      ⚠️ At least one communication preference must be selected.
                    </Typography>
                  )}
                  {missingMarketingConsent && (
                    <Typography variant="body2" color="error" sx={{ mb: 1 }}>
                      ⚠️ Marketing consent is required. Map a marketing consent header or mark it as
                      implied.
                    </Typography>
                  )}
                </Box>
              )}
              {isLoading && (
                <Box
                  sx={{
                    margin: "20px auto",
                    border: "4px solid #f3f3f3",
                    borderTop: "4px solid #007bff",
                    borderRadius: "50%",
                    width: "30px",
                    height: "30px",
                    animation: "spin 1s linear infinite",
                    "@keyframes spin": {
                      to: {
                        transform: "rotate(360deg)",
                      },
                    },
                  }}
                />
              )}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  width: "100%",
                  mt: 2,
                }}
              >
                <Button
                  type="submit"
                  disabled={
                    !isValidated ||
                    !formValues.campaignName ||
                    !formValues.country ||
                    missingRequired.length > 0 ||
                    missingHashMapping ||
                    isDialingCodeRequired ||
                    missingCommPreference ||
                    missingMarketingConsent
                  }
                  onClick={handleSubmit}
                  color="success"
                  name="Submit"
                  variant="contained"
                />
              </Box>
            </>
          )}
        </Box>
      </Paper>
    </Container>
  );
};

export default BatchForm;
